import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_CONTRAT_STATUT_OPTIONS,
  AGENCE_CONTRAT_STATUT_TONES,
  agenceContratReference,
  agenceLocataireNomComplet,
  type AgenceContrat,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";
import ContratForm from "./components/ContratForm";

type SortKey = "recent" | "loyer" | "debut";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "loyer", label: "Loyer décroissant" },
  { value: "debut", label: "Date de début" },
];

const statutOptions = [{ value: "", label: "Tous les statuts" }, ...AGENCE_CONTRAT_STATUT_OPTIONS];

export default function AgenceContratsPage() {
  const { contrats, immeubles, biens, locataires, getBien, getLocataire, getImmeuble, removeContrat, paiementsByContrat } =
    useAgenceData();
  const { proprietaires } = useProprietaires();
  const { can } = useEmployes();
  const [searchParams] = useSearchParams();

  const proprietaireParam = searchParams.get("proprietaire") ?? "";
  const immeubleParam = searchParams.get("immeuble") ?? "";
  const bienParam = searchParams.get("bien") ?? "";
  const locataireParam = searchParams.get("locataire") ?? "";
  const openFromQuery = searchParams.get("nouveau") === "1";

  const [formOpen, setFormOpen] = useState(openFromQuery);
  const [search, setSearch] = useState("");
  const [proprietaireFilter, setProprietaireFilter] = useState(proprietaireParam);
  const [immeubleFilter, setImmeubleFilter] = useState(immeubleParam);
  const [bienFilter, setBienFilter] = useState(bienParam);
  const [locataireFilter, setLocataireFilter] = useState(locataireParam);
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<AgenceContrat | null>(null);
  const [toDelete, setToDelete] = useState<AgenceContrat | null>(null);

  const hasPrerequis = immeubles.length > 0 && biens.length > 0 && locataires.length > 0;
  const hasContrats = contrats.length > 0;

  const stats = useMemo(
    () => ({
      total: contrats.length,
      actifs: contrats.filter((item) => item.statut === "actif").length,
      brouillons: contrats.filter((item) => item.statut === "brouillon").length,
      expires: contrats.filter((item) => item.statut === "expire").length,
    }),
    [contrats]
  );

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...immeubles
        .filter((immeuble) => !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    ],
    [immeubles, proprietaireFilter]
  );

  const bienOptions = useMemo(
    () => [
      { value: "", label: "Tous les biens" },
      ...biens
        .filter((bien) => !immeubleFilter || bien.immeubleId === immeubleFilter)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    ],
    [biens, immeubleFilter]
  );

  const locataireOptions = useMemo(
    () => [
      { value: "", label: "Tous les locataires" },
      ...locataires.map((item) => ({ value: item.id, label: agenceLocataireNomComplet(item) })),
    ],
    [locataires]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = contrats.filter((contrat) => {
      const matchesProprietaire = !proprietaireFilter || contrat.proprietaireId === proprietaireFilter;
      const matchesImmeuble = !immeubleFilter || contrat.immeubleId === immeubleFilter;
      const matchesBien = !bienFilter || contrat.bienId === bienFilter;
      const matchesLocataire = !locataireFilter || contrat.locataireId === locataireFilter;
      const matchesStatut = !statutFilter || contrat.statut === statutFilter;

      const bien = getBien(contrat.bienId);
      const locataire = getLocataire(contrat.locataireId);
      const immeuble = getImmeuble(contrat.immeubleId);
      const owner = proprietaires.find((item) => item.id === contrat.proprietaireId);
      const matchesSearch =
        !term ||
        [
          agenceContratReference(contrat.id),
          bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          locataire ? agenceLocataireNomComplet(locataire) : "",
          immeuble?.nom ?? "",
          owner ? proprietaireNomComplet(owner) : "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return (
        matchesProprietaire &&
        matchesImmeuble &&
        matchesBien &&
        matchesLocataire &&
        matchesStatut &&
        matchesSearch
      );
    });

    return result.sort((a, b) => {
      if (sort === "loyer") return b.loyerMensuel - a.loyerMensuel;
      if (sort === "debut") return new Date(b.dateDebut).getTime() - new Date(a.dateDebut).getTime();
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [
    contrats,
    proprietaires,
    search,
    proprietaireFilter,
    immeubleFilter,
    bienFilter,
    locataireFilter,
    statutFilter,
    sort,
    getBien,
    getLocataire,
    getImmeuble,
  ]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(proprietaireFilter) ||
    Boolean(immeubleFilter) ||
    Boolean(bienFilter) ||
    Boolean(locataireFilter) ||
    Boolean(statutFilter);

  const handleAdd = () => setFormOpen(true);

  const exportColumns: ExportColumn<AgenceContrat>[] = [
    { header: "Référence", value: (contrat) => agenceContratReference(contrat.id) },
    {
      header: "Propriétaire",
      value: (contrat) => {
        const owner = proprietaires.find((item) => item.id === contrat.proprietaireId);
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    { header: "Immeuble", value: (contrat) => getImmeuble(contrat.immeubleId)?.nom ?? "" },
    {
      header: "Bien",
      value: (contrat) => {
        const bien = getBien(contrat.bienId);
        return bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    {
      header: "Locataire",
      value: (contrat) => {
        const locataire = getLocataire(contrat.locataireId);
        return locataire ? agenceLocataireNomComplet(locataire) : "";
      },
    },
    { header: "Date de début", value: (contrat) => formatDate(contrat.dateDebut) },
    { header: "Date de fin", value: (contrat) => formatDate(contrat.dateFin) },
    { header: "Loyer mensuel (FCFA)", value: (contrat) => formatFcfa(contrat.loyerMensuel) },
    { header: "Caution (FCFA)", value: (contrat) => formatFcfa(contrat.caution) },
    { header: "Statut", value: (contrat) => AGENCE_CONTRAT_STATUT_LABELS[contrat.statut] },
  ];

  const columns: DataTableColumn<AgenceContrat>[] = [
    {
      key: "reference",
      header: "Référence",
      className: "min-w-[130px]",
      render: (contrat) => (
        <span className="font-label font-semibold text-foreground-950">{agenceContratReference(contrat.id)}</span>
      ),
    },
    {
      key: "locataire",
      header: "Locataire",
      className: "min-w-[180px]",
      render: (contrat) => {
        const locataire = getLocataire(contrat.locataireId);
        return <span className="text-foreground-700">{locataire ? agenceLocataireNomComplet(locataire) : "Non renseigné"}</span>;
      },
    },
    {
      key: "bien",
      header: "Bien · Immeuble",
      className: "min-w-[200px]",
      render: (contrat) => {
        const bien = getBien(contrat.bienId);
        const immeuble = getImmeuble(contrat.immeubleId);
        return (
          <div className="flex flex-col">
            <span className="truncate text-foreground-800">{bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "Bien non renseigné"}</span>
            <span className="truncate text-xs text-foreground-500">{immeuble ? immeuble.nom : "Immeuble non renseigné"}</span>
          </div>
        );
      },
    },
    {
      key: "proprietaire",
      header: "Propriétaire",
      className: "min-w-[180px]",
      render: (contrat) => {
        const owner = proprietaires.find((item) => item.id === contrat.proprietaireId);
        return <span className="text-foreground-700">{owner ? proprietaireNomComplet(owner) : "Non renseigné"}</span>;
      },
    },
    {
      key: "loyer",
      header: "Loyer / mois",
      className: "min-w-[130px]",
      render: (contrat) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(contrat.loyerMensuel)}</span>
      ),
    },
    {
      key: "statut",
      header: "Statut",
      render: (contrat) => (
        <Badge tone={AGENCE_CONTRAT_STATUT_TONES[contrat.statut]}>{AGENCE_CONTRAT_STATUT_LABELS[contrat.statut]}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[220px]",
      render: (contrat) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/agence/contrats/${contrat.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions
            onEdit={can("contrats.modifier") ? () => setEditing(contrat) : undefined}
            onDelete={can("contrats.supprimer") ? () => setToDelete(contrat) : undefined}
          />
        </div>
      ),
    },
  ];

  const resetFilters = () => {
    setSearch("");
    setProprietaireFilter("");
    setImmeubleFilter("");
    setBienFilter("");
    setLocataireFilter("");
    setStatutFilter("");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Contrats</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Créez et suivez les baux : loyer, caution, avance, jour d'échéance et statut. Chaque contrat respecte les
            relations propriétaire → immeuble → bien, puis relie un locataire à un bien.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-contrats" title="Contrats" />
          {hasPrerequis ? (
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un contrat
            </button>
          ) : (
            <Link
              to="/agence/immeubles"
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-building-2-line text-base" aria-hidden="true" />
              Préparer un bien
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-file-text-line" label="Contrats" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Contrats actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-draft-line" label="Brouillons" value={String(stats.brouillons)} tone="accent" />
        <StatCard icon="ri-history-line" label="Contrats expirés" value={String(stats.expires)} tone="neutral" />
      </div>

      {hasContrats && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-ctr-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="agence-ctr-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Référence, propriétaire, bien, locataire, immeuble..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-ctr-proprietaire-filter" className="text-xs font-label font-medium text-foreground-700">
                Propriétaire
              </label>
              <Select
                id="agence-ctr-proprietaire-filter"
                value={proprietaireFilter}
                onChange={(event) => {
                  setProprietaireFilter(event.target.value);
                  setImmeubleFilter("");
                  setBienFilter("");
                }}
                options={proprietaireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-ctr-immeuble-filter" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="agence-ctr-immeuble-filter"
                value={immeubleFilter}
                onChange={(event) => {
                  setImmeubleFilter(event.target.value);
                  setBienFilter("");
                }}
                options={immeubleOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-ctr-bien-filter" className="text-xs font-label font-medium text-foreground-700">
                Bien
              </label>
              <Select
                id="agence-ctr-bien-filter"
                value={bienFilter}
                onChange={(event) => setBienFilter(event.target.value)}
                options={bienOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-ctr-locataire-filter" className="text-xs font-label font-medium text-foreground-700">
                Locataire
              </label>
              <Select
                id="agence-ctr-locataire-filter"
                value={locataireFilter}
                onChange={(event) => setLocataireFilter(event.target.value)}
                options={locataireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-ctr-statut-filter" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="agence-ctr-statut-filter"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={statutOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-ctr-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="agence-ctr-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} contrat{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {contrats.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {!hasPrerequis && (
        <EmptyState
          icon="ri-file-text-line"
          title="Préparez d'abord immeuble, bien et locataire"
          description="Un contrat relie un locataire à un bien d'un immeuble. Créez ces éléments dans leurs modules avant d'enregistrer un contrat."
          action={
            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to="/agence/immeubles"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-building-2-line text-base" aria-hidden="true" />
                Immeubles
              </Link>
              <Link
                to="/agence/locataires"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-user-3-line text-base" aria-hidden="true" />
                Locataires
              </Link>
            </div>
          }
        />
      )}

      {hasPrerequis && !hasContrats && (
        <EmptyState
          icon="ri-file-add-line"
          title="Aucun contrat pour le moment"
          description="Enregistrez votre premier contrat en sélectionnant le propriétaire, l'immeuble, le bien et le locataire, puis le loyer et les échéances."
          action={
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un contrat
            </button>
          }
        />
      )}

      {hasContrats && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun contrat ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(contrat) => contrat.id}
          minWidthClass="min-w-[1080px]"
          caption="Contrats"
        />
      )}

      {hasContrats && filtered.length > 0 && (
        <p className="text-center text-xs text-foreground-400">
          Statuts disponibles : {Object.values(AGENCE_CONTRAT_STATUT_LABELS).join(" · ")}.
        </p>
      )}

      <ContratForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        defaultProprietaireId={proprietaireParam || undefined}
        defaultImmeubleId={immeubleParam || undefined}
        defaultBienId={bienParam || undefined}
        defaultLocataireId={locataireParam || undefined}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeContrat(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer le contrat"
        description={
          toDelete ? `Voulez-vous vraiment supprimer « ${agenceContratReference(toDelete.id)} » ? Cette action est définitive.` : ""
        }
        blockedMessage={
          toDelete && paiementsByContrat(toDelete.id).length > 0
            ? `Ce contrat comporte ${paiementsByContrat(toDelete.id).length} paiement(s). Supprimez d'abord ses paiements.`
            : undefined
        }
      />
    </div>
  );
}